import { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  OutlinedInput,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { StandardDialog } from '../base';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { createVehicle, VehiclePayload } from '../../../services/vehicleService';
import { Vehicle, VehicleType, SwappableBodyType, TrailerType, TrailerProperty, TrailerEquipment, LoadSecurity, TrackerProviders } from '../../../models/vehicle';
import { useTranslation } from 'react-i18next';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import DateTimeInput from '../../inputs/DateTimeInput';
import { SelectInput } from '../../inputs';
import { enumToOptions } from '../../../utils/misc';
import { handleApiError } from '../../../services/apiClient';
import { formatDateTimeLocal } from '../../../utils/dateTime';
import { parseCoordinates, formatCoordinates } from '../../../utils/coordinateUtils';
import { useTenant } from '../../../hooks';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialData?: VehiclePayload | null;
  onSuccess?: () => void;
};

// Validation schema
const validationSchema = yup.object({
  truckPlate: yup.string().required('Truck plate is required'),
  trailerPlate: yup.string().optional(),
  name: yup.string().required('Truck name is required'),
  manufacturer: yup.string().required('Manufacturer is required'),
  model: yup.string().required('Model is required'),
  modelYear: yup.number().required('Model year is required').min(1900, 'Invalid year').max(new Date().getFullYear() + 1, 'Year cannot be in the future'),
  colour: yup.string().required('Color is required'),
  chassisNumber: yup.string().required('Chassis number is required'),
  type: yup.string().required('Type is required'),
  trailerType: yup.string().required('Trailer type is required'),
  swapBodyType: yup.string().when('swappable', {
    is: true,
    then: (schema) => schema.required('Swap body type is required when swappable is enabled'),
    otherwise: (schema) => schema.optional()
  }),
  trailerProperty: yup.array().optional(),
  equipment: yup.array().optional(),
  loadSecurity: yup.array().optional(),
  emptyAt: yup.string().optional(),
  isActive: yup.boolean().optional(),
  maximumWeightT: yup.number().optional().min(0, 'Weight must be positive'),
  maximumLengthM: yup.number().optional().min(0, 'Length must be positive'),
  regCountry: yup.string().required('Registration country is required'),
  maximumCabotages: yup.number().required('Maximum cabotages is required').min(0, 'Cabotages must be non-negative'),
  tenantPreferredRoute: yup.string().required('Assigned route is required'),
});

export default function RegisterVehicleDialog({ isOpen, onClose, initialData, onSuccess }: Props) {
  const { t } = useTranslation();
  const { tenant } = useTenant();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantRoutes, setTenantRoutes] = useState<string[]>([]);

  useEffect(() => {
    if (tenant?.preferredRoutes) {
      setTenantRoutes(Object.keys(tenant.preferredRoutes));
    }
  }, [tenant]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm<VehiclePayload>({
    resolver: yupResolver(validationSchema) as any,
  });

  const swappableValue = watch('swappable');

  useEffect(() => {
    if (!swappableValue) {
      setValue('swapBodyType', undefined);
    }
  }, [swappableValue, setValue]);

  // Effect to populate form with mock data when modal opens
  useEffect(() => {
    // Reset states when modal opens
    if (isOpen) {
      setError(null);
    }

    if (initialData && isOpen) {
      reset({
        truckPlate: initialData.truckPlate || '',
        trailerPlate: initialData.trailerPlate || '',
        name: initialData.name || '',
        manufacturer: initialData.manufacturer || '',
        model: initialData.model || '',
        modelYear: initialData.modelYear || undefined,
        colour: initialData.colour || '',
        chassisNumber: initialData.chassisNumber || '',
        type: initialData.type || VehicleType.TRAILER,
        swappable: initialData.swappable || false,
        swapBodyType: initialData.swapBodyType || undefined,
        trailerProperty: initialData.trailerProperty || [],
        trailerType: initialData.trailerType || TrailerType.TAUTLINER,
        equipment: initialData.equipment || [],
        loadSecurity: initialData.loadSecurity,
        currentPosition: initialData.currentPosition || {
          objectType: 'Address',
          city: '',
          country: CountryCode.DE,
          geoCoordinate: {
            longitude: 0,
            latitude: 0
          },
          geocoded: false,
          postalCode: '',
          streetOrPostbox: ''
        },
        emptyIn: initialData.emptyIn || {
          objectType: 'Address',
          city: '',
          country: CountryCode.DE,
          geoCoordinate: {
            longitude: 0,
            latitude: 0
          },
          geocoded: false,
          postalCode: '',
          streetOrPostbox: ''
        },
        emptyAt: initialData.emptyAt || undefined,
        isActive: initialData.isActive || true,
        maximumWeightT: initialData.maximumWeightT || undefined,
        maximumLengthM: initialData.maximumLengthM || undefined,
        regCountry: initialData.regCountry,
        maximumCabotages: initialData.maximumCabotages || 0,
        tenantPreferredRoute: initialData.tenantPreferredRoute || '',
        tracker: initialData.tracker || undefined
      });
    } else if (!initialData && isOpen) {
      // Reset to empty form when no mock data
      reset({
        truckPlate: '',
        trailerPlate: '',
        name: '',
        manufacturer: '',
        model: '',
        modelYear: undefined,
        colour: '',
        chassisNumber: '',
        type: VehicleType.TRAILER,
        swappable: false,
        swapBodyType: undefined,
        trailerProperty: [],
        trailerType: TrailerType.CURTAIN_SIDER,
        equipment: [],
        loadSecurity: [],
        currentPosition: {
          objectType: 'Address',
          city: '',
          country: CountryCode.DE,
          geoCoordinate: {
            longitude: 0,
            latitude: 0
          },
          geocoded: false,
          postalCode: '',
          streetOrPostbox: ''
        },
        emptyIn: {
          objectType: 'Address',
          city: '',
          country: CountryCode.DE,
          geoCoordinate: {
            longitude: 0,
            latitude: 0
          },
          geocoded: false,
          postalCode: '',
          streetOrPostbox: ''
        },
        emptyAt: undefined,
        isActive: true,
        maximumWeightT: 25,
        maximumLengthM: 13.6,
        regCountry: '',
        maximumCabotages: 0,
        tenantPreferredRoute: '',
        tracker: undefined
      });
    }
  }, [initialData, isOpen, reset]);

  const onSubmit = async (values: Vehicle) => {
    setLoading(true);
    setError(null);

    try {
      const payload: VehiclePayload = {
        truckPlate: values.truckPlate,
        trailerPlate: values.trailerPlate,
        name: values.name,
        manufacturer: values.manufacturer,
        model: values.model,
        modelYear: values.modelYear ? Number(values.modelYear) : undefined,
        colour: values.colour,
        chassisNumber: values.chassisNumber,
        type: values.type,
        swappable: values.swappable,
        swapBodyType: values.swapBodyType,
        trailerProperty: values.trailerProperty,
        trailerType: values.trailerType,
        equipment: values.equipment,
        loadSecurity: values.loadSecurity,
        currentPosition: values.currentPosition ? {
          ...values.currentPosition,
          streetOrPostbox: values.currentPosition.streetOrPostbox || ''
        } : undefined,
        emptyIn: values.emptyIn ? {
          ...values.emptyIn,
          streetOrPostbox: values.emptyIn.streetOrPostbox || ''
        } : undefined,
        emptyAt: values.emptyAt ? new Date(values.emptyAt).getTime() : undefined,
        isActive: values.isActive,
        maximumWeightT: values.maximumWeightT,
        maximumLengthM: values.maximumLengthM,
        regCountry: values.regCountry,
        maximumCabotages: values.maximumCabotages,
        tenantPreferredRoute: values.tenantPreferredRoute,
        tracker: values.tracker,
      };

      await createVehicle(payload);
      reset();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
      // toast.success(t('Truck added successfully'));
    } catch (err: any) {
      handleApiError(err, t('Failed to add truck'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StandardDialog
      open={isOpen}
      onClose={onClose}
      title={t('Add Truck')}
      subtitle={t('Add a new truck to your fleet')}
      maxWidth="md"
      width={{ xs: '100%', sm: '90%', md: '700px', lg: '750px' }}
      height={{ xs: '100vh', sm: '95vh', md: '90vh', lg: '85vh' }}
      error={error}
      primaryAction={{
        label: loading ? t('Adding...') : t('Add Truck'),
        onClick: handleSubmit(onSubmit as any),
        loading: loading,
        disabled: loading
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose
      }}
      contentSx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
    >
      <Box sx={{ flex: 1 }}>
        {/* Basic Information */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.dark', mb: 2 }}>
              🚛 {t('Basic Information')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="truckPlate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Truck Plate')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.truckPlate}
                      helperText={errors.truckPlate?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="trailerPlate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Trailer Plate')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.trailerPlate}
                      helperText={errors.trailerPlate?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Truck Name')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="manufacturer"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Manufacturer')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.manufacturer}
                      helperText={errors.manufacturer?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="model"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Model')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.model}
                      helperText={errors.model?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="modelYear"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Model Year')}
                      required
                      type="number"
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.modelYear}
                      helperText={errors.modelYear?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="colour"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Color')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.colour}
                      helperText={errors.colour?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="chassisNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Chassis Number')}
                      required
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.chassisNumber}
                      helperText={errors.chassisNumber?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Trailer & Equipment */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.dark', mb: 2 }}>
              🚚 {t('Trailer & Equipment')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="trailerType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.trailerType} sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <SelectInput
                        {...field}
                        label={t('Trailer Type')}
                        required
                        options={enumToOptions(TrailerType)}
                        InputLabelProps={{ shrink: !!field.value }}
                      />
                      {errors.trailerType && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.trailerType.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.type} sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <SelectInput
                        {...field}
                        label={t('Type')}
                        required
                        options={enumToOptions(VehicleType)}
                        InputLabelProps={{ shrink: !!field.value }}
                      />
                      {errors.type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.type.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="tracker"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.tracker} sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <SelectInput
                        {...field}
                        label={t('Tracker Provider')}
                        options={TrackerProviders.map(provider => ({ key: provider, value: provider }))}
                        InputLabelProps={{ shrink: !!field.value }}
                      />
                      {errors.tracker && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.tracker.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="swappable"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                        />
                      }
                      label={t('Swappable Body')}
                    />
                  )}
                />
              </Box>
              {swappableValue && (
                <Box sx={{ flex: '1 1 300px' }}>
                  <Controller
                    name="swapBodyType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.swapBodyType} sx={{
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}>
                        <InputLabel>{t('Swap Body Type')}</InputLabel>
                        <Select
                          {...field}
                          label={t('Swap Body Type')}
                          required={!!swappableValue}
                          sx={{
                            borderRadius: 2,
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'text.black'
                            }
                          }}
                        >
                          {Object.values(SwappableBodyType).map((type) => (
                            <MenuItem key={type} value={type}>
                              {type.replaceAll('_', ' ').toUpperCase()}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.swapBodyType && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {errors.swapBodyType.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Equipment & Security */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.dark', mb: 2 }}>
              🔧 {t('Equipment & Security')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="equipment"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <InputLabel>{t('Equipment')}</InputLabel>
                      <Select
                        multiple
                        {...field}
                        input={<OutlinedInput label={t('Equipment')} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as (keyof typeof TrailerEquipment)[]).map((value) => (
                              <Chip key={value} label={TrailerEquipment[value] || value} />
                            ))}
                          </Box>
                        )}
                        sx={{
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        }}
                      >
                        {Object.values(TrailerEquipment).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.replaceAll('_', ' ').toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="loadSecurity"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <InputLabel>{t('Load Security')}</InputLabel>
                      <Select
                        multiple
                        {...field}
                        input={<OutlinedInput label={t('Load Security')} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as (keyof typeof LoadSecurity)[]).map((value) => (
                              <Chip key={value} label={LoadSecurity[value] || value} />
                            ))}
                          </Box>
                        )}
                        sx={{
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        }}
                      >
                        {Object.values(LoadSecurity).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.replaceAll('_', ' ').toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="trailerProperty"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <InputLabel>{t('Trailer Properties')}</InputLabel>
                      <Select
                        multiple
                        {...field}
                        input={<OutlinedInput label={t('Trailer Properties')} />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as (keyof typeof TrailerProperty)[]).map((value) => (
                              <Chip key={value} label={TrailerProperty[value] || value} />
                            ))}
                          </Box>
                        )}
                        sx={{
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        }}
                      >
                        {Object.values(TrailerProperty).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.replaceAll('_', ' ').toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Vehicle Specifications */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid border.green' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              ⚖️ {t('Vehicle Specifications')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.grey', mb: 3, fontStyle: 'italic' }}>
              {t('Technical specifications and operational limits')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                        />
                      }
                      label={t('Vehicle Active')}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="maximumWeightT"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Maximum Weight (Tons)')}
                      type="number"
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.maximumWeightT}
                      helperText={errors.maximumWeightT?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="maximumLengthM"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Maximum Length (Meters)')}
                      type="number"
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.maximumLengthM}
                      helperText={errors.maximumLengthM?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="regCountry"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.regCountry} sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <SelectInput
                        {...field}
                        label={t('Registration Country')}
                        required
                        options={Object.values(CountryCode).map((code) => ({ key: COUNTRY_NAMES[code], value: code }))}
                        InputLabelProps={{ shrink: !!field.value }}
                      />
                      {errors.regCountry && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.regCountry.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="maximumCabotages"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Maximum Cabotages')}
                      required
                      type="number"
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      error={!!errors.maximumCabotages}
                      helperText={errors.maximumCabotages?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Route Preference */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.dark', mb: 2 }}>
              🧭 {t('Route Preference')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="tenantPreferredRoute"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.tenantPreferredRoute} sx={{
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'text.black'
                      }
                    }}>
                      <SelectInput
                        {...field}
                        label={t('Assigned Route')}
                        required
                        options={tenantRoutes.map((routeKey) => ({ key: routeKey, value: routeKey.replaceAll('_', ' ') }))}
                        InputLabelProps={{ shrink: !!field.value }}
                      />
                      {errors.tenantPreferredRoute && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.tenantPreferredRoute.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Current Position */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid border.blueLight' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              📍 {t('Current Position')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.grey', mb: 3, fontStyle: 'italic' }}>
              {t('Where the truck is currently located')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="currentPosition.city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('City')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="currentPosition.postalCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Postal Code')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="currentPosition.geoCoordinate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={t('Coordinates (lat, lng)')}
                      placeholder={t('e.g., 55.58886621397866, 23.765426156862443')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      value={formatCoordinates(field.value?.latitude || 0, field.value?.longitude || 0)}
                      onChange={(e) => {
                        const coords = parseCoordinates(e.target.value);
                        field.onChange({
                          latitude: coords.latitude,
                          longitude: coords.longitude
                        });
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Empty In Position */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid border.purple' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              🚛 {t('Empty In Position')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.grey', mb: 3, fontStyle: 'italic' }}>
              {t('Where the truck will be empty/unloaded')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="emptyIn.city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('City')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="emptyIn.postalCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('Postal Code')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="emptyIn.geoCoordinate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={t('Coordinates (lat, lng)')}
                      placeholder={t('e.g., 55.58886621397866, 23.765426156862443')}
                      fullWidth
                      onKeyDown={e => e.key === 'Enter' && handleSubmit(onSubmit as any)}
                      value={formatCoordinates(field.value?.latitude || 0, field.value?.longitude || 0)}
                      onChange={(e) => {
                        const coords = parseCoordinates(e.target.value);
                        field.onChange({
                          latitude: coords.latitude,
                          longitude: coords.longitude
                        });
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Controller
                  name="emptyAt"
                  control={control}
                  render={({ field }) => (
                    <DateTimeInput
                      {...field}
                      label={t('Empty At (Date & Time)')}
                      type="datetime"
                      fullWidth
                      value={formatDateTimeLocal(field.value)}
                      onChange={(value) => {
                        const timestamp = value && typeof value === 'object' && 'toDate' in value 
                          ? value.toDate().getTime() 
                          : (value as any) instanceof Date 
                            ? (value as any as Date).getTime() 
                            : 0;
                        field.onChange(timestamp);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'text.black'
                          }
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'text.black'
                        }
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </StandardDialog>
  );
}

